// Serviço de autenticação para comunicação com a API
import apiService from "../shared/apiService";
import {
  clearAuthStorage,
  enforceSessionPolicy,
  redirectToLogin,
  setSessionPreference,
} from "../shared/authUtils";
import { API_ENDPOINTS } from "../shared/constants";
import { User } from "../types/auth";

export interface LoginResponse {
  success: boolean;
  message: string;
  access_token: string;
  refresh_token: string;
  tokens?: {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
  };
  user: User;
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  user_id: string;
  email_sent: boolean;
}

export interface ApiError {
  error: string;
  requires_password_setup?: boolean;
}

// ===== reCAPTCHA v3 (invisível) — cadastro e reset de senha =====
// Sem VITE_RECAPTCHA_SITE_KEY o captcha fica desligado (o backend também, sem a secret).

type Grecaptcha = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

const RECAPTCHA_SITE_KEY = String(import.meta.env.VITE_RECAPTCHA_SITE_KEY ?? "")
  .trim()
  .replace(/^['"]|['"]$/g, "");

let recaptchaScript: Promise<Grecaptcha> | null = null;

const loadRecaptcha = (): Promise<Grecaptcha> => {
  recaptchaScript ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`;
    script.async = true;
    script.onload = () => {
      const grecaptcha = (window as unknown as { grecaptcha?: Grecaptcha })
        .grecaptcha;
      if (grecaptcha) grecaptcha.ready(() => resolve(grecaptcha));
      else reject(new Error("reCAPTCHA não carregou"));
    };
    script.onerror = () => {
      recaptchaScript = null; // permite tentar de novo no próximo envio
      reject(new Error("reCAPTCHA não carregou"));
    };
    document.head.appendChild(script);
  });
  return recaptchaScript;
};

/** Token do reCAPTCHA para `action` (deve bater com a action validada no backend). */
const getRecaptchaToken = async (
  action: "register" | "forgot_password",
): Promise<string | undefined> => {
  if (!RECAPTCHA_SITE_KEY) return undefined;
  try {
    const grecaptcha = await loadRecaptcha();
    return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
  } catch {
    // Bloqueador de anúncios etc.: segue sem token e o backend decide.
    return undefined;
  }
};

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    // Sessões "só neste navegador" de uma abertura anterior são encerradas aqui
    enforceSessionPolicy();
    // Carregar tokens do localStorage na inicialização
    this.loadTokensFromStorage();
    // Registrar renovador de token no apiService para evitar dependência circular
    apiService.setTokenRenewer(this.renewToken.bind(this));
  }

  // ===== MÉTODOS PRIVADOS =====

  private saveTokensToStorage(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
  }

  private loadTokensFromStorage(): void {
    this.accessToken = localStorage.getItem("access_token");
    this.refreshToken = localStorage.getItem("refresh_token");
  }

  private clearTokensFromStorage(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("token"); // Limpar token antigo também
  }

  // ===== MÉTODOS DE AUTENTICAÇÃO =====

  async login(
    email: string,
    password: string,
    remember = true,
  ): Promise<LoginResponse> {
    // Limpar tokens antigos antes de tentar login
    this.clearTokensFromStorage();

    // Backend retorna { user: {...}, token: "..." }
    const backendResponse = (await apiService.post(
      API_ENDPOINTS.AUTH.LOGIN,
      {
        email,
        password,
      },
      { requiresAuth: false },
    )) as { user: User; token: string };

    // Mapear para o formato esperado pelo frontend
    const response: LoginResponse = {
      success: true,
      message: "Login realizado com sucesso",
      access_token: backendResponse.token, // Backend retorna 'token', não 'access_token'
      refresh_token: backendResponse.token, // Por enquanto, usar o mesmo token
      user: backendResponse.user,
    };

    // Salvar dados no storage
    this.saveTokensToStorage(response.access_token, response.refresh_token);
    localStorage.setItem("user", JSON.stringify(response.user));
    setSessionPreference(remember);

    return response;
  }

  async register(
    email: string,
    name: string,
    password: string,
    company?: string,
    phone?: string,
  ): Promise<RegisterResponse> {
    return await apiService.post<RegisterResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      {
        email,
        name,
        password,
        company,
        phone,
        captcha_token: await getRecaptchaToken("register"),
      },
      { requiresAuth: false },
    );
  }

  async setPassword(token: string, password: string): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>(
      API_ENDPOINTS.AUTH.SET_PASSWORD,
      {
        token,
        password,
      },
      { requiresAuth: false },
    );

    if (response.success) {
      this.saveTokensToStorage(response.access_token, response.refresh_token);
      localStorage.setItem("user", JSON.stringify(response.user));
    }

    return response;
  }

  async confirmEmail(token: string, password: string): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>(
      API_ENDPOINTS.AUTH.CONFIRM_EMAIL,
      {
        token,
        password,
      },
      { requiresAuth: false },
    );

    if (response.success) {
      this.saveTokensToStorage(response.access_token, response.refresh_token);
      localStorage.setItem("user", JSON.stringify(response.user));
    }

    return response;
  }

  async forgotPassword(
    email: string,
    redirectUri?: string | null,
  ): Promise<{ success: boolean; message: string }> {
    try {
      await apiService.post(
        API_ENDPOINTS.AUTH.REQUEST_RESET,
        {
          email,
          redirect_uri: redirectUri,
          captcha_token: await getRecaptchaToken("forgot_password"),
        },
        { requiresAuth: false },
      );
      return {
        success: true,
        message:
          "Se o e-mail estiver cadastrado, você receberá instruções para redefinir sua senha.",
      };
    } catch (error: any) {
      // Para segurança, sempre retornamos sucesso mesmo se o usuário não existir
      // Isso evita vazamento de informação sobre quais emails estão cadastrados
      if (
        error.message.includes("404") ||
        error.message.includes("User not found")
      ) {
        return {
          success: true,
          message:
            "Se o e-mail estiver cadastrado, você receberá instruções para redefinir sua senha.",
        };
      }
      // Para outros erros, retornamos falha
      return {
        success: false,
        message: "Erro ao processar solicitação. Tente novamente.",
      };
    }
  }

  async validateResetToken(
    token: string,
  ): Promise<{ email: string; name: string } | null> {
    try {
      return await apiService.get<{ email: string; name: string }>(
        API_ENDPOINTS.AUTH.VALIDATE_RESET_TOKEN,
        { token },
        { requiresAuth: false },
      );
    } catch {
      return null;
    }
  }

  async resetPassword(
    token: string,
    password: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.AUTH.RESET_PASSWORD,
        { token, new_password: password },
        { requiresAuth: false },
      );
      // Backend retorna { message: "Password reset successfully", user: {...} } em caso de sucesso
      return {
        success: true,
        message:
          (response as { message: string }).message ||
          "Senha redefinida com sucesso!",
      };
    } catch (error: any) {
      // Em caso de erro, apiService lança exception
      return {
        success: false,
        message: error.message || "Erro ao redefinir senha",
      };
    }
  }

  async renewToken(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const response = await apiService.post(
        API_ENDPOINTS.AUTH.REFRESH,
        {
          refresh_token: this.refreshToken,
        },
        { requiresAuth: false, retry: false },
      );

      if (response && response.access_token) {
        this.accessToken = response.access_token;
        localStorage.setItem("access_token", response.access_token);
        return true;
      }

      // Se refresh falhou, fazer logout
      this.logout();
      return false;
    } catch (error) {
      console.error("Erro ao renovar token:", error);
      this.logout();
      return false;
    }
  }

  logout(redirectToLoginFlag: boolean = true): void {
    clearAuthStorage();

    // Se for logout automático por backend indisponível, redirecionar para login
    if (redirectToLoginFlag) {
      redirectToLogin();
    }
  }

  // ===== MÉTODOS DE PERFIL =====

  async getProfile(): Promise<{ success: boolean; user: User }> {
    return await apiService.get(API_ENDPOINTS.AUTH.ME);
  }

  async updateProfile(
    name: string,
    company?: string,
    phone?: string,
  ): Promise<{ success: boolean; user: User }> {
    return await apiService.put(API_ENDPOINTS.AUTH.UPDATE_PROFILE, {
      name,
      company,
      phone,
    });
  }

  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; message: string }> {
    return await apiService.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  async getPendingUsers(): Promise<{ success: boolean; users: User[] }> {
    return await apiService.get(API_ENDPOINTS.AUTH.PENDING_USERS);
  }

  async approveUser(
    uuid: string,
  ): Promise<{ success: boolean; message: string; user: User }> {
    const url = API_ENDPOINTS.AUTH.APPROVE_USER.replace("{uuid}", uuid);
    return await apiService.post(url, {});
  }

  // ===== UTILITÁRIOS =====

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  async checkApiHealth(): Promise<{
    status: string;
    message: string;
    database: string;
    email_configured: boolean;
  }> {
    return await apiService.get(API_ENDPOINTS.AUTH.HEALTH, undefined, {
      requiresAuth: false,
    });
  }

  // ===== VALIDAÇÕES =====

  static validateEmail(email: string): boolean {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  }

  static validatePassword(password: string): {
    valid: boolean;
    message?: string;
  } {
    if (password.length < 8) {
      return {
        valid: false,
        message: "Senha deve ter pelo menos 8 caracteres",
      };
    }

    if (!/[A-Z]/.test(password)) {
      return {
        valid: false,
        message: "Senha deve ter pelo menos uma letra maiúscula",
      };
    }

    if (!/[a-z]/.test(password)) {
      return {
        valid: false,
        message: "Senha deve ter pelo menos uma letra minúscula",
      };
    }

    if (!/\d/.test(password)) {
      return { valid: false, message: "Senha deve ter pelo menos um número" };
    }

    return { valid: true };
  }
}

// Instância singleton do serviço
const authService = new AuthService();
export default authService;
